import { useDispatch } from 'react-redux';
import { ElementAction } from '../../../elements/elementAction';
import { ContentPlanRootState as S } from '../../../elements/contentPlan/store';
import { Functions } from '../../../types/types';
import { Metadata } from '../../../types';
import { ThunkDispatch } from 'redux-thunk';
import { reloadData } from '../administrationActions';
import { readFileAsText } from '../../../utils/file';
import { Toasters } from '../../Toaster/toaster';
import { sleep } from 'rcd-cpp-workspace/utils/sleep';
import { API } from '../../../api';
import { BacktagData, BacktagImport } from '../../../api/backtag';
import { ApiResponseMessage } from '../../../utils/constants';

const getStagedTagsItemCount =
  (data: Partial<Metadata>): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { getStagedTagsItemCount } = getAPI();
    return await getStagedTagsItemCount({ data });
  };

const publishStagedTags =
  (data: Partial<Metadata>): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { publishStagedTags } = getAPI();
    await publishStagedTags({ data });
    dispatch(reloadData());
  };

const toBacktagData = (content: string): BacktagData[] => {
  if (!!content) {
    const data = content
      .replaceAll('\r', '\n')
      .split('\n')
      .map(x => x.trim())
      .filter(x => x)
      .map(x => x.split(',').slice(0, 5));
    return data
      .map(d => {
        if (/^[\d| |\|]+$/.test(d.join(''))) {
          const [itemId, tcIds, ttIds, rpIds, pjsIds] = d;
          return {
            itemId: toInt(itemId),
            topicCategoryIds: toIntArray(tcIds),
            taxonomyTagIds: toIntArray(ttIds),
            rolePriorityIds: toIntArray(rpIds),
            priorityJourneyStepIds: toIntArray(pjsIds)
          };
        }
      })
      .filter(x => x);
  }
  return null;
};

const toInt = (s: string) => {
  const n = parseInt(s?.trim());
  if (isNaN(n)) return null;
  return n;
};
const toIntArray = (s: string) =>
  s
    ?.split('|')
    .map(toInt)
    .filter(x => x);

const importBacktagData =
  (
    backtagImport: BacktagImport,
    backtagData: BacktagData[],
    toasters: Toasters<Partial<Metadata>>,
    closeModal: Function
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { importBacktagData, getBacktagImport } = getAPI();
    const toaster = toasters.createToaster();
    const toasterId = toaster.id;
    let failureCount = 0;
    await Promise.all([
      importBacktagData(toasterId, { backtagData, backtagImport }).catch(
        err => {
          if (err.message != ApiResponseMessage.EndpointRequestTimedOut) {
            failureCount = 10;
            throw err;
          }
        }
      ),
      pollBacktagImport(
        toasterId,
        { getBacktagImport },
        async backtagImport => {
          if (backtagImport) {
            failureCount = 0;
            closeModal();
            const status = backtagImport?.props?.status;
            if (status == 'Failed') {
              toaster.error(backtagImport);
              return false;
            } else if (status == 'Done') {
              const { missingKIIds, missingCTIds } = backtagImport.props;
              if (missingKIIds?.length || missingCTIds?.length) {
                toaster.warn(backtagImport);
              } else {
                toaster.success(backtagImport);
              }
              return false;
            }
            if (!backtagImport.props.message) {
              backtagImport.props.message =
                'Upload in progress. You will receive an email with additional details once completed.';
            }
            toaster.show(backtagImport, true);
          } else {
            failureCount++;
            if (failureCount > 3) {
              return false;
            }
            await sleep(failureCount * 2000);
          }
          return true;
        }
      )
    ]);
  };

const pollBacktagImport = async (
  toasterId: string,
  { getBacktagImport }: Pick<API, 'getBacktagImport'>,
  onResponse: (backtagImport: Metadata) => Promise<boolean>
) => {
  const maxAttempts = 180;
  let attempts = 0;
  while (++attempts < maxAttempts) {
    await sleep(3000);
    const backtagImport = await getBacktagImport(toasterId);
    const continuePolling = await onResponse(backtagImport);
    if (!continuePolling) return;
  }
};

const validateFile =
  (file: File, validFileExtensions?: string[]): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (validFileExtensions) {
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (!validFileExtensions.includes(`.${extension}`))
        throw new Error(
          `Invalid file type. Valid file types: ${validFileExtensions.join(
            ', '
          )}`
        );
    }

    const fileContent = await readFileAsText(file);

    if (!fileContent?.trim()) throw new Error('File is Empty');
    const backtagData = toBacktagData(fileContent);
    const uniqueItems = new Set<number>();
    const uniqueTCs = new Set<number>();
    const uniqueTTs = new Set<number>();
    const uniqueRPs = new Set<number>();
    const uniqueJSs = new Set<number>();

    backtagData.forEach(
      ({
        itemId,
        topicCategoryIds,
        taxonomyTagIds,
        rolePriorityIds,
        priorityJourneyStepIds
      }) => {
        if (itemId) uniqueItems.add(itemId);
        topicCategoryIds?.forEach(tcId => uniqueTCs.add(tcId));
        taxonomyTagIds?.forEach(ttId => uniqueTTs.add(ttId));
        rolePriorityIds?.forEach(rpId => uniqueRPs.add(rpId));
        priorityJourneyStepIds?.forEach(jsId => uniqueJSs.add(jsId));
      }
    );
    const { validateBacktagImportData } = getAPI();
    const resp = await validateBacktagImportData({
      itemIds: [...uniqueItems],
      tcIds: [...uniqueTCs],
      ttIds: [...uniqueTTs],
      rpIds: [...uniqueRPs],
      jsIds: [...uniqueJSs]
    });
    const report = {
      'Total Staged Items': uniqueItems.size,
      'Total Staged Topic Category Ids': uniqueTCs.size,
      'Missing Staged Topic Category Ids': resp.missingTCIds?.length
        ? resp.missingTCIds.join(', ')
        : null,
      'Total Staged Perennial Priority Ids': uniqueTTs.size,
      'Missing Staged Perennial Priority Ids': resp.missingTTIds?.length
        ? resp.missingTTIds.join(', ')
        : null,
      'Total Staged Mission-Critical Priority Ids': uniqueRPs.size,
      'Missing Staged Mission-Critical Priority Ids': resp.missingRPIds?.length
        ? resp.missingRPIds.join(', ')
        : null,
      'Total Staged Journey Step Ids': uniqueJSs.size,
      'Missing Staged Journey Step Ids': resp.missingJSIds?.length
        ? resp.missingJSIds.join(', ')
        : null
    };
    return {
      backtagData,
      backtagImport: {
        ...resp,
        fileName: file.name,
        totalItems: uniqueItems.size,
        totalTCs: uniqueTCs.size,
        totalTTs: uniqueTTs.size,
        totalRPs: uniqueRPs.size,
        totalJSs: uniqueJSs.size
      },
      report
    };
  };

export const useActions = () => {
  const dispatch: ThunkDispatch<S, any, any> = useDispatch();
  return {
    reloadData: () => dispatch(reloadData()),
    getStagedTagsItemCount: async (d: Partial<Metadata>) =>
      await dispatch(getStagedTagsItemCount(d)),
    publishStagedTags: async (d: Partial<Metadata>) =>
      await dispatch(publishStagedTags(d)),
    importBacktagData: async (
      f: BacktagImport,
      d: BacktagData[],
      t: Toasters<Metadata>,
      closeModal: Function
    ) => await dispatch(importBacktagData(f, d, t, closeModal)),
    validateFile: async (f: File, validFileExtensions?: string[]) =>
      await dispatch(validateFile(f, validFileExtensions))
  };
};
